<template>
    <common-modal v-if="isOpen" width="large" height="medium" class="upload-hipaa-modal" @close="handleClose">
        <div>
            <h3 class="upload-hipaa-modal__title">
                {{ $t('modals.patient_profile.profile.hippa_detail.upload.head') }}
            </h3>

            <form-wrapper class="practice-information-form">
                <form-row>
                    <form-cell width="half">
                        <label class="form-field__label">
                            {{ $t('forms.patient_profile.fields.hipaa.label') }}
                        </label>
                        <text-field
                            :placeholder="$t('forms.patient_profile.fields.hipaa.insurance_plans.placeholder')"
                            :isRequired="false"
                            :formData="data"
                            valuePath="authorizationName"
                            @change="change($event)"
                        ></text-field>
                    </form-cell>
                    <form-cell width="half">
                        <label class="form-field__label">
                            {{ $t('forms.patient_profile.fields.hipaa.document.label') }}
                        </label>
                        <text-field
                            :placeholder="$t('forms.patient_profile.fields.hipaa.document.placeholder')"
                            :isRequired="false"
                            :formData="data"
                            :minDate="minSelectableDate"
                            valuePath="expiryDate"
                            type="date"
                            @change="change($event)"
                        ></text-field>
                    </form-cell>
                </form-row>
                <form-row>
                    <form-cell width="half">
                        <label class="form-field__label">
                            {{ $t('forms.patient_profile.fields.hipaa.provider.label') }}
                        </label>
                        <div v-clickaway="closeDropDown" class="auth-prov-wrapper" :class="{ focused: isAuthProvFocused }">
                            <div class="chips">
                                <span
                                    v-for="user in selectedAuthProviders"
                                    :key="user.id"
                                    class="chip"
                                    @click.stop="unselectUser(user)"
                                >
                                    {{ `${user.firstName} ${user.lastName}` }}
                                    <common-icon name="close" size="tiny" class="remove"></common-icon>
                                </span>
                            </div>
                            <input
                                v-model="searchTerm"
                                type="text"
                                class="auth-prov"
                                :placeholder="searchProviderPlaceholder"
                                @keyup="inputChange"
                            />
                        </div>

                        <common-dropdown
                            :recalculatePosition="true"
                            :isOpen="showAuthProv"
                            :hasArrow="false"
                            arrowPositionY="top"
                            arrowPositionX="right"
                        >
                            <div class="network-users">
                                <div
                                    v-for="(user, index) in items"
                                    :key="index"
                                    :class="getNetworkUsersClass(user)"
                                    @click="selectUser(user)"
                                >
                                    <div>
                                        <common-user-avatar
                                            size="user-menu"
                                            :user="user.user"
                                            :isUserProfileLinkEnabled="false"
                                        ></common-user-avatar>
                                    </div>
                                    <div>
                                        <p>
                                            {{ user.user ? user.user.firstName + ' ' + user.user.lastName : user.practice.name }}
                                        </p>
                                        <p v-if="user.user">{{ providerSpecialization(user?.specialization) }}</p>
                                    </div>
                                </div>

                                <div v-if="isLoading" class="loading-spinner">
                                    <span>{{ $t('modals.import_contacts_modal.loading') }}</span>
                                    <common-logo-spinner :width="56"></common-logo-spinner>
                                </div>

                                <div v-else-if="hasSearched && items.length === 0">
                                    <p class="search-network-members__message">
                                        {{ $t('modals.search_network_members.no_member_found') }}
                                    </p>
                                </div>
                            </div>
                        </common-dropdown>
                    </form-cell>
                    <form-cell width="half">
                        <label class="form-field__label">
                            {{ $t('forms.patient_profile.fields.hipaa.comment.label') }}
                        </label>
                        <select-input
                            class="schedule-apply__input"
                            :placeholder="$t('forms.patient_profile.fields.hipaa.comment.placeholder')"
                            :hasAutofocus="true"
                            :options="scopeOfAuthorizationOptions"
                            :value="scopeId"
                            @change="changeScope($event)"
                        ></select-input>
                    </form-cell>
                </form-row>
            </form-wrapper>

            <div v-if="file" class="selected-files-wrapper">
                <div class="selected-file">
                    <common-icon name="file" size="medium" class="selected-file__icon"></common-icon>

                    <div class="selected-file__info">
                        <p class="selected-file__name">
                            {{ file.name }}
                        </p>

                        <p class="selected-file__size">
                            {{ formatFileSize(file.size / 1024) }}
                        </p>
                    </div>
                </div>
            </div>

            <div class="modal-footer">
                <common-button
                    :label="$t('common.microcopy.cancel')"
                    type="secondary"
                    size="large"
                    :disabled="isSubmitting"
                    @click="handleClose"
                ></common-button>
                <common-button
                    :label="$t('modals.patient_profile.profile.hippa_detail.upload.button')"
                    type="primary"
                    size="large"
                    :isProcessing="isSubmitting"
                    :disabled="isSubmitting || !file"
                    @click="submitModal"
                ></common-button>
            </div>
        </div>
        <common-notification ref="commonSuccessNotificationChild" :content="notificationMessage"></common-notification>
    </common-modal>
</template>

<script>
    import FormMixin from 'mixins/FormMixin'
    import HandleRequestMixin from 'mixins/HandleRequestMixin'
    import debounce from 'lodash.debounce'
    import config from '../../../config'
    import ProviderDatabaseAPI from 'api/ProviderDatabaseAPI'
    import PatientRecordsAPI from '../../../api/PatientRecordsAPI'
    import dayjs from 'dayjs'

    export default {
        mixins: [FormMixin, HandleRequestMixin],

        props: {
            isOpen: { type: Boolean, required: true },
            file: { type: [Object, File], default: null },
        },

        data() {
            return {
                scopeOfAuthorizationOptions: [
                    { label: 'Complete Medical Records', value: 1 },
                    { label: 'General Medical (No Mental Health)', value: 2 },
                    { label: 'Mental Health Records Only', value: 3 },
                    { label: 'Lab & Imaging Only', value: 4 },
                    { label: 'Prescription History Only', value: 5 },
                ],
                scopeId: null,
                showAuthProv: false,
                isAuthProvFocused: false,
                selectedAuthProviders: [],
                items: [],
                searchTerm: '',
                isLoading: false,
                hasSearched: false,
                isSubmitting: false,
                notificationMessage: '',
            }
        },

        computed: {
            searchProviderPlaceholder() {
                return this.selectedAuthProviders.length ? '' : this.$t('forms.patient_profile.fields.hipaa.provider.placeholder')
            },
            selectedScope() {
                return this.scopeOfAuthorizationOptions.find((item) => item.value === this.scopeId)
            },
            minSelectableDate() {
                return dayjs().format('YYYY-MM-DD')
            },
        },

        methods: {
            initDefaultProvider() {
                const user = this.$auth.getUser()
                if (user && !this.selectedAuthProviders.length) {
                    this.selectedAuthProviders = [
                        {
                            id: user.id,
                            firstName: user.firstName,
                            lastName: user.lastName,
                            npi: user.npi,
                            businessEmail: user.businessEmail,
                        },
                    ]
                }
            },
            getNetworkUsersClass(user) {
                return ['network-user', { 'network-user-selected': user ? this.selectedAuthProviders[user.id] : false }]
            },
            changeScope(event) {
                this.scopeId = event
            },
            closeDropDown() {
                this.showAuthProv = false
                this.isAuthProvFocused = false
            },
            inputChange: debounce(function () {
                this.emitEvent({ name: 'input-change' })
                if (this.searchTerm.length < 3) {
                    this.items = []
                    this.hasSearched = false
                    this.isLoading = false
                    this.showAuthProv = false
                    return
                }
                this.showAuthProv = true
                this.isAuthProvFocused = true
                this.hasSearched = true
                this.isLoading = true

                ProviderDatabaseAPI.search({
                    query: this.searchTerm,
                })
                    .then((val) => {
                        this.items = Array.isArray(val) ? val : []
                    })
                    .catch(() => {
                        this.items = []
                    })
                    .finally(() => {
                        this.isLoading = false
                    })
            }, config.DEBOUNCE.DEFAULT),

            selectUser(user) {
                if (!user) return
                const exists = this.selectedAuthProviders.find((item) => item.id === user.id)
                if (exists) return

                this.selectedAuthProviders.push({
                    id: user.id,
                    firstName: user.firstName,
                    lastName: user.lastName,
                    npi: user.npi,
                    businessEmail: user.businessEmail,
                })

                this.searchTerm = ''
                this.items = []
            },

            unselectUser(ev) {
                this.selectedAuthProviders.forEach((user, index) => {
                    if (user.id === ev.id) {
                        this.selectedAuthProviders.splice(index, 1)
                    }
                })
            },

            providerSpecialization(value) {
                if (!value) return ''
                return value
                    .split('-')
                    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
                    .join(' ')
            },

            getBase64(file) {
                return new Promise((resolve, reject) => {
                    const reader = new FileReader()
                    reader.readAsDataURL(file)
                    reader.onload = () => resolve(reader.result)
                    reader.onerror = (error) => reject(error)
                })
            },

            formatFileSize(kb) {
                if (!kb) return '0 KB'
                if (kb < 1024) return `${Math.round(kb)} KB`
                return `${(kb / 1024).toFixed(1)} MB`
            },

            handleClose() {
                this.emitEvent({ name: 'close' })
            },

            async submitModal() {
                if (!this.file || this.isSubmitting) return
                this.isSubmitting = true
                this.notificationMessage = ''

                try {
                    const UploadsAPI = (await import('api/UploadsAPI')).default
                    const [uploaded] = await UploadsAPI.uploadFiles([this.file], 'patient-hipaa')
                    const payload = {
                        patientId: Number(this.$route.params.id),
                        authorizationName: this.data.authorizationName,
                        scope: this.selectedScope?.label,
                        fromUserId: Number(this.$auth.getActiveMember()?.id),
                        fromPracticeId: Number(this.$auth.getUser()?.practiceId),
                        expiryDate: this.data.expiryDate,
                        authorizedProviders: this.selectedAuthProviders,
                        file: {
                            uploadKey: uploaded.uploadKey,
                            fileName: uploaded.fileName,
                            size: uploaded.size,
                        },
                    }

                    await PatientRecordsAPI.createHipaaForm(payload)
                    this.notificationMessage = this.$t('modals.patient_profile.profile.hippa_detail.notification')
                    this.$refs.commonSuccessNotificationChild.showNotification()
                    this.emitEvent({ name: 'refresh-hipaa' })
                    setTimeout(() => {
                        this.handleClose()
                    }, 500)
                } catch (error) {
                    // handle error if needed
                } finally {
                    this.isSubmitting = false
                }
            },
        },

        watch: {
            file: {
                immediate: true,
                handler(newFile) {
                    if (newFile && newFile.name) {
                        this.data.authorizationName = newFile.name.replace(/\.[^/.]+$/, '')
                    }
                },
            },
            isOpen(val) {
                if (val) {
                    this.initDefaultProvider()
                }
            },
        },

        mounted() {
            this.initDefaultProvider()
        },
    }
</script>

<style scoped lang="scss">
    @import 'styles/setup/_variables.scss';
    @import 'styles/setup/_mixins.scss';

    .upload-hipaa-modal {
        &__title {
            font-size: 18px;
            font-weight: 500;
            color: $color-dark-2;
            margin-bottom: 20px;
        }
    }

    .modal-footer {
        display: flex;
        justify-content: flex-end;
        gap: 12px;
        margin-top: 24px;
    }

    .selected-files-wrapper {
        width: 100%;
        margin-top: 16px;
    }

    .selected-file {
        display: flex;
        align-items: center;
        gap: 12px;
        padding: 12px;
        background: #f0fdf4;
        border: 1px solid #bbf7d0;
        border-radius: 8px;

        &__icon {
            flex-shrink: 0;
            color: #16a34a;
        }

        &__info {
            flex: 1;
            min-width: 0;
        }

        &__name {
            margin: 0;
            color: #166534;
            font-size: 14px;
            font-weight: 500;
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
        }

        &__size {
            margin: 2px 0 0;
            color: #16a34a;
            font-size: 12px;
        }
    }
    .auth-prov-wrapper {
        display: flex;
        flex-wrap: wrap;
        align-items: center;
        min-height: 50px;
        padding: 15px 10px;
        border: 1px solid #ccc;
        border-radius: 4px;
        cursor: text;
        transition: border-color 0.2s ease;
    }

    .auth-prov-wrapper.focused {
        border-color: $color--accent-1;
    }
    .auth-prov-wrapper .chips {
        display: flex;
        flex-wrap: wrap;
        gap: 6px;
    }
    .auth-prov::placeholder {
        color: #b9b9b9 !important;
        opacity: 1;
    }

    .chip {
        display: flex;
        align-items: center;
        padding: 4px 8px;
        background: $color--accent-1;
        border-radius: 12px;
        font-size: 13px;
        color: #fff;
    }

    .chip .remove {
        margin-left: 10px;
        cursor: pointer;
        font-weight: bold;
    }

    .auth-prov {
        border: none;
        outline: none;
        flex: 1 1 150px;
        width: 100%;
        min-width: 120px;
        font-size: 14px;
        margin-left: 8px;
        color: $color--txt-label;
    }
    .network-users {
        overflow-y: scroll;
        max-height: 210px;
    }
    .network-user {
        display: flex;
        padding: 10px;
        cursor: pointer;
    }
    .network-user > div:last-child {
        @include breakpoint(smartphone-large) {
            width: 200px;
        }
        width: 300px;
        margin-left: 15px;
    }
    .network-user > div:first-child {
        margin-left: 30px;
    }
    .network-user p:first-child {
        font-weight: 500;
    }
    .network-user p:last-child {
        font-weight: 200;
    }
    .network-user:hover {
        background-color: #f5fbfe;
    }
    .network-user-selected {
        background-color: #e8f7ff;
    }

    .search-network-members__message {
        margin: 30px;
        text-align: center;
    }

    .loading-spinner {
        margin: 30px;
        text-align: center;
        font-size: 16px;
        padding: 32px 16px;
    }
</style>
